import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { crm } from "../../../../theme";
import {
  ProcessDocumentScan,
  scanProcessDocument,
} from "../../../../services/processService";

export type ScanFieldKey = Exclude<keyof ProcessDocumentScan, "documentType">;

export const scanFieldLabels: Record<ScanFieldKey, string> = {
  blAwbCrt: "BL/AWB/CRT",
  vessel: "Embarcação",
  customsAgent: "Armador",
  dischargePort: "Porto de Descarga",
  shippingDate: "Data de embarque",
  arrivalDate: "Data de chegada",
  containerNumber: "Nº do contêiner",
  containerQuantity: "Total de Contêineres",
  exporter: "Exportador",
  incoterm: "Incoterm",
  countryOfOrigin: "País de Origem",
};

const MAX_BYTES = 10 * 1024 * 1024;

const fmt = (key: ScanFieldKey, v?: string) => {
  if (!v) return "";
  if ((key === "shippingDate" || key === "arrivalDate") && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [y, m, d] = v.split("-");
    return `${d}/${m}/${y}`;
  }
  return v;
};

const ScanDocumentModal = ({
  isOpen,
  onClose,
  getCurrent,
  normalize,
  onApply,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Valor atual do form para cada campo (string ou vazio). */
  getCurrent: (key: ScanFieldKey) => string;
  /** Ajusta a sugestão ao que o form aceita (ex.: país da lista). null = descarta. */
  normalize: (key: ScanFieldKey, value: string) => string | null;
  onApply: (values: Partial<Record<ScanFieldKey, string>>) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<ProcessDocumentScan>();
  const [rows, setRows] = useState<
    { key: ScanFieldKey; current: string; suggested: string }[]
  >([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setError(undefined);
      setResult(undefined);
      setRows([]);
      setChecked({});
    }
  }, [isOpen]);

  const handleFile = async (file?: File) => {
    if (!file) return;
    setError(undefined);
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "png", "jpg", "jpeg"].includes(ext ?? "")) {
      setError("Formato inválido. Envie PDF, PNG ou JPG.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Arquivo maior que 10MB.");
      return;
    }
    setLoading(true);
    try {
      const data = await scanProcessDocument(file);
      const next: { key: ScanFieldKey; current: string; suggested: string }[] = [];
      (Object.keys(scanFieldLabels) as ScanFieldKey[]).forEach((key) => {
        const raw = data[key];
        if (!raw) return;
        const suggested = normalize(key, raw);
        if (!suggested) return;
        next.push({ key, current: getCurrent(key), suggested });
      });
      setResult(data);
      setRows(next);
      // Marca por padrão só os que diferem do atual; campo já preenchido exige opt-in.
      const c: Record<string, boolean> = {};
      next.forEach((r) => {
        c[r.key] = !r.current && r.current !== r.suggested;
      });
      setChecked(c);
    } catch (e: any) {
      const msg = e?.response?.data;
      setError(
        typeof msg === "string" && msg
          ? msg
          : "Não foi possível ler o documento. Tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const selectedCount = rows.filter((r) => checked[r.key]).length;

  const apply = () => {
    const values: Partial<Record<ScanFieldKey, string>> = {};
    rows.forEach((r) => {
      if (checked[r.key]) values[r.key] = r.suggested;
    });
    onApply(values);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="3xl" scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(2px)" bg="blackAlpha.600" />
      <ModalContent borderRadius="16px" boxShadow="0 12px 32px rgba(16,24,40,0.18)">
        <ModalHeader borderBottom={crm.border}>Ler documento (IA)</ModalHeader>
        <ModalCloseButton />
        <ModalBody py="16px">
          {!result && (
            <Box>
              <Text fontSize="sm" color={"gray.600"} mb="12px">
                Envie o BL, AWB ou CRT (PDF, PNG ou JPG, até 10MB). Os valores
                sugeridos só entram no formulário depois da sua confirmação, e o
                processo só é salvo quando você clicar em salvar.
              </Text>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                pt="4px"
                isDisabled={loading}
                onChange={(e) => {
                  handleFile(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
              {loading && (
                <Flex align="center" gap="8px" mt="16px">
                  <Spinner size="sm" />
                  <Text fontSize="sm">Lendo documento, pode levar alguns segundos...</Text>
                </Flex>
              )}
            </Box>
          )}
          {error && (
            <Alert status="error" mt="12px" borderRadius="8px">
              <AlertIcon />
              {error}
            </Alert>
          )}
          {result && (
            <Box>
              <Flex align="center" gap="8px" mb="12px">
                <Text fontSize="sm">Documento detectado:</Text>
                <Badge>{result.documentType ?? "OTHER"}</Badge>
              </Flex>
              {rows.length === 0 ? (
                <Alert status="info" borderRadius="8px">
                  <AlertIcon />
                  Nenhum campo reconhecido neste documento.
                </Alert>
              ) : (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th w="40px"></Th>
                      <Th>Campo</Th>
                      <Th>Valor atual</Th>
                      <Th>Valor sugerido</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => (
                      <Tr key={r.key}>
                        <Td>
                          <Checkbox
                            isChecked={!!checked[r.key]}
                            aria-label={scanFieldLabels[r.key]}
                            onChange={(e) =>
                              setChecked((c) => ({ ...c, [r.key]: e.target.checked }))
                            }
                          />
                        </Td>
                        <Td>{scanFieldLabels[r.key]}</Td>
                        <Td color="gray.500" wordBreak="break-word">
                          {fmt(r.key, r.current) || "—"}
                        </Td>
                        <Td fontWeight="600" wordBreak="break-word">
                          {fmt(r.key, r.suggested)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          )}
        </ModalBody>
        <ModalFooter gap="8px" borderTop={crm.border}>
          <Button variant="outline" borderColor={crm.border} color={crm.text} borderRadius="10px" onClick={onClose}>
            Cancelar
          </Button>
          {result && (
            <Button
              variant="outline"
              borderRadius="10px"
              onClick={() => {
                setResult(undefined);
                setRows([]);
              }}
            >
              Outro arquivo
            </Button>
          )}
          {result && rows.length > 0 && (
            <Button
              backgroundColor={crm.brand}
              _hover={{ backgroundColor: crm.brandDark }}
              color="white"
              borderRadius="10px"
              isDisabled={selectedCount === 0}
              onClick={apply}
            >
              Aplicar ({selectedCount})
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ScanDocumentModal;
